const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BECH32_ALPHABET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function sha256(bytes: Uint8Array): Uint8Array {
  // Minimal pure-JS SHA-256 for double-hash base58check validation.
  // Implemented locally to avoid a runtime dependency on Node's crypto
  // inside code shared between browser and Netlify Functions.
  const k = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
    0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
    0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
    0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
    0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
    0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
    0xc67178f2,
  ]);
  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const bitLen = bytes.length * 8;
  const withOne = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  withOne.set(bytes);
  withOne[bytes.length] = 0x80;
  const view = new DataView(withOne.buffer);
  view.setUint32(withOne.length - 4, bitLen >>> 0);
  view.setUint32(withOne.length - 8, Math.floor(bitLen / 0x100000000));

  const w = new Uint32Array(64);
  for (let offset = 0; offset < withOne.length; offset += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = view.getUint32(offset + i * 4);
    }
    for (let i = 16; i < 64; i++) {
      const s0 =
        ((w[i - 15] >>> 7) | (w[i - 15] << 25)) ^
        ((w[i - 15] >>> 18) | (w[i - 15] << 14)) ^
        (w[i - 15] >>> 3);
      const s1 =
        ((w[i - 2] >>> 17) | (w[i - 2] << 15)) ^
        ((w[i - 2] >>> 19) | (w[i - 2] << 13)) ^
        (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let i = 0; i < 64; i++) {
      const s1 =
        ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + k[i] + w[i]) | 0;
      const s0 =
        ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) | 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  [h0, h1, h2, h3, h4, h5, h6, h7].forEach((word, i) =>
    outView.setUint32(i * 4, word >>> 0),
  );
  return out;
}

function doubleSha256(bytes: Uint8Array): Uint8Array {
  return sha256(sha256(bytes));
}

function decodeBase58(input: string): Uint8Array | null {
  let num = 0n;
  const base = 58n;
  for (const char of input) {
    const index = BASE58_ALPHABET.indexOf(char);
    if (index === -1) return null;
    num = num * base + BigInt(index);
  }

  let hex = num.toString(16);
  if (hex.length % 2) hex = '0' + hex;
  const bytes: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.slice(i, i + 2), 16));
  }

  let leadingZeros = 0;
  for (const char of input) {
    if (char === '1') leadingZeros++;
    else break;
  }

  return new Uint8Array([...new Array(leadingZeros).fill(0), ...bytes]);
}

function isValidBase58Check(address: string, expectedVersions: number[]): boolean {
  if (address.length < 25 || address.length > 34) return false;
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return false;

  const payload = decoded.slice(0, decoded.length - 4);
  const checksum = decoded.slice(decoded.length - 4);
  const hash = doubleSha256(payload);

  for (let i = 0; i < 4; i++) {
    if (hash[i] !== checksum[i]) return false;
  }

  return expectedVersions.includes(payload[0]);
}

function bech32Polymod(values: number[]): number {
  const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const value of values) {
    const top = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ value;
    for (let i = 0; i < 5; i++) {
      if ((top >>> i) & 1) chk ^= GEN[i];
    }
  }
  return chk;
}

function bech32HrpExpand(hrp: string): number[] {
  const result: number[] = [];
  for (const char of hrp) result.push(char.charCodeAt(0) >> 5);
  result.push(0);
  for (const char of hrp) result.push(char.charCodeAt(0) & 31);
  return result;
}

function isValidBech32(address: string): boolean {
  const lower = address.toLowerCase();
  const upper = address.toUpperCase();
  if (address !== lower && address !== upper) return false;

  const str = lower;
  const separator = str.lastIndexOf('1');
  if (separator < 1 || separator + 7 > str.length || str.length > 90) return false;

  const hrp = str.slice(0, separator);
  if (hrp !== 'bc') return false;

  const dataPart = str.slice(separator + 1);
  const values: number[] = [];
  for (const char of dataPart) {
    const index = BECH32_ALPHABET.indexOf(char);
    if (index === -1) return false;
    values.push(index);
  }

  const witnessVersion = values[0];
  const constant = witnessVersion === 0 ? 1 : 0x2bc830a3;
  const polymod = bech32Polymod([...bech32HrpExpand(hrp), ...values]);
  if (polymod !== constant) return false;

  const program = values.slice(1, values.length - 6);
  if (program.length < 1) return false;

  return true;
}

const TESTNET_PREFIXES = ['m', 'n', '2', 'tb1'];

export interface AddressValidationResult {
  valid: boolean;
  reason?: string;
}

export function validateBitcoinAddress(rawInput: string): AddressValidationResult {
  const address = rawInput.trim();

  if (address.length === 0) {
    return { valid: false, reason: 'Address is empty.' };
  }

  if (address.length < 25 || address.length > 90) {
    return { valid: false, reason: 'Address length is out of bounds.' };
  }

  for (const prefix of TESTNET_PREFIXES) {
    if (address.toLowerCase().startsWith(prefix) && prefix.length > 1) {
      return { valid: false, reason: 'Testnet addresses are not supported.' };
    }
  }

  if (address.startsWith('1') || address.startsWith('3')) {
    const versions = address.startsWith('1') ? [0x00] : [0x05];
    if (isValidBase58Check(address, versions)) {
      return { valid: true };
    }
    return { valid: false, reason: 'Checksum validation failed.' };
  }

  if (address.toLowerCase().startsWith('bc1')) {
    if (isValidBech32(address)) {
      return { valid: true };
    }
    return { valid: false, reason: 'Bech32 checksum validation failed.' };
  }

  return { valid: false, reason: 'Unrecognized Bitcoin mainnet address format.' };
}

export function shortenAddress(address: string, visible = 6): string {
  if (address.length <= visible * 2 + 3) return address;
  return `${address.slice(0, visible)}…${address.slice(-visible)}`;
}
